import React, { useCallback, useEffect, useState } from 'react';
import { Eye, Link2, Loader2, RefreshCw, Stethoscope } from 'lucide-react';
import toast from 'react-hot-toast';
import CopyLinkButton from './CopyLinkButton';
import {
  ActionMenuDivider,
  ActionMenuItem,
  ActionsDropdownMenu,
} from './ActionsDropdownMenu';
import DeepLinkSeededCampaignsViewModal, {
  type SeededCampaignViewEntry,
} from './DeepLinkSeededCampaignsViewModal';
import {
  dlBtnGhostClass,
  dlBtnPrimaryClass,
  dlCardClass,
  dlInputClass,
  dlStatCardClass,
  dlTableCellClass,
  dlTableHeadClass,
  dlTableRowClass,
  dlTableWrapClass,
} from './deepLinkUi';
import type {
  DeepLinkConsultationMode,
  DeepLinkDoctorSeedItem,
  DeepLinkDoctorSeedList,
} from '../../../models/MasterAdmin/deepLink';
import {
  fetchDeepLinkDoctorSeedList,
  seedAllDeepLinkDoctors,
  seedDeepLinkDoctor,
} from '../../../services/MasterAdmin/deepLinkService';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';

function statusBadge(status: DeepLinkDoctorSeedItem['seedStatus']) {
  if (status === 'done') {
    return (
      <span className="rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
        Done
      </span>
    );
  }
  if (status === 'partial') {
    return (
      <span className="rounded-md bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
        Partial
      </span>
    );
  }
  return (
    <span className="rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
      Pending
    </span>
  );
}

function modeLabel(mode: DeepLinkConsultationMode) {
  return mode === 'in-clinic' ? 'In-clinic' : 'Online';
}

function doctorViewEntries(item: DeepLinkDoctorSeedItem): SeededCampaignViewEntry[] {
  const entries: SeededCampaignViewEntry[] = [];
  for (const mode of ['online', 'in-clinic'] as DeepLinkConsultationMode[]) {
    const campaign = item.modes[mode]?.campaign;
    if (campaign?.id) {
      entries.push({ label: modeLabel(mode), campaign });
    }
  }
  return entries;
}

const DeepLinkDoctorSeedPanel: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [data, setData] = useState<DeepLinkDoctorSeedList | null>(null);
  const [seedingKey, setSeedingKey] = useState<string | null>(null);
  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null);
  const [viewItem, setViewItem] = useState<DeepLinkDoctorSeedItem | null>(null);

  const load = useCallback(async (q = search) => {
    setLoading(true);
    try {
      const res = await fetchDeepLinkDoctorSeedList({ search: q, limit: 200 });
      setData(res);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to load doctors'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    void load('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSeed = async (item: DeepLinkDoctorSeedItem, modes: DeepLinkConsultationMode[]) => {
    const key = `${item.vendorId}:${modes.join(',')}`;
    if (seedingKey) return;
    setSeedingKey(key);
    setOpenMenuKey(null);
    try {
      const res = await seedDeepLinkDoctor(item.vendorId, modes);
      toast.success(
        `Seeded ${res.created.length} link(s) for ${res.doctorName}${
          res.skipped.length ? ` (${res.skipped.length} already existed)` : ''
        }`
      );
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Seed failed'));
    } finally {
      setSeedingKey(null);
    }
  };

  const handleSeedAll = async (modes?: DeepLinkConsultationMode[]) => {
    if (seedingKey) return;
    const key = modes?.length ? `all:${modes.join(',')}` : 'all:both';
    setSeedingKey(key);
    setOpenMenuKey(null);
    try {
      const res = await seedAllDeepLinkDoctors(modes);
      toast.success(
        `Seeded ${res.createdCount} link(s) across ${res.doctorsTouched} doctor(s)${
          res.skippedCount ? `, skipped ${res.skippedCount}` : ''
        }${res.errorCount ? `, ${res.errorCount} error(s)` : ''}`
      );
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Bulk seed failed'));
    } finally {
      setSeedingKey(null);
    }
  };

  const busy = Boolean(seedingKey);
  const viewEntries = viewItem ? doctorViewEntries(viewItem) : [];

  return (
    <div className="w-full space-y-4">
      <div className={`${dlCardClass} p-4`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white">
              <Stethoscope className="h-4 w-4 text-[#128C7E]" />
              Doctor deep links
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
              Seed User-app doctor profile campaigns. Short links appear after seeding — use View for
              full links and analytics.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={busy}
              className={dlBtnGhostClass}
              onClick={() => void handleSeedAll(['online'])}
            >
              {seedingKey === 'all:online' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Seed all online
            </button>
            <button
              type="button"
              disabled={busy}
              className={dlBtnGhostClass}
              onClick={() => void handleSeedAll(['in-clinic'])}
            >
              {seedingKey === 'all:in-clinic' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Seed all in-clinic
            </button>
            <button
              type="button"
              disabled={busy}
              className={dlBtnPrimaryClass}
              onClick={() => void handleSeedAll()}
            >
              {seedingKey === 'all:both' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Seed all (both)
            </button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className={dlStatCardClass}>
            <p className="text-xs text-gray-500">Doctors</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">{data?.total ?? '—'}</p>
          </div>
          <div className={dlStatCardClass}>
            <p className="text-xs text-gray-500">Pending</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">
              {data?.pendingDoctors ?? '—'}
            </p>
          </div>
          <div className={dlStatCardClass}>
            <p className="text-xs text-gray-500">Done</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">{data?.doneDoctors ?? '—'}</p>
          </div>
          <div className={dlStatCardClass}>
            <p className="text-xs text-gray-500">Campaigns</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">
              {data?.seededCampaigns ?? '—'}
            </p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void load(search);
            }}
            placeholder="Search doctor, clinic, city…"
            className={`${dlInputClass} max-w-md`}
          />
          <button type="button" className={dlBtnGhostClass} onClick={() => void load(search)}>
            Search
          </button>
          <button
            type="button"
            className={dlBtnGhostClass}
            onClick={() => {
              setSearch('');
              void load('');
            }}
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading doctors…
        </div>
      ) : (
        <div className={`${dlTableWrapClass} w-full`}>
          <table className="w-full table-fixed">
            <colgroup>
              <col className="w-[26%]" />
              <col className="w-[14%]" />
              <col className="w-[10%]" />
              <col className="w-[40%]" />
              <col className="w-[10%]" />
            </colgroup>
            <thead>
              <tr>
                <th className={`${dlTableHeadClass} ${dlTableCellClass}`}>Doctor</th>
                <th className={`${dlTableHeadClass} ${dlTableCellClass}`}>Modes</th>
                <th className={`${dlTableHeadClass} ${dlTableCellClass}`}>Status</th>
                <th className={`${dlTableHeadClass} ${dlTableCellClass}`}>Short links</th>
                <th className={`${dlTableHeadClass} ${dlTableCellClass} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items || []).length === 0 ? (
                <tr>
                  <td colSpan={5} className={`${dlTableCellClass} text-gray-500`}>
                    No doctors found
                  </td>
                </tr>
              ) : (
                (data?.items || []).map((item) => {
                  const pendingModes = item.availableModes.filter(
                    (mode) => !item.modes[mode]?.seeded
                  );
                  const menuKey = `doctor-${item.vendorId}`;
                  const seededEntries = doctorViewEntries(item);
                  return (
                    <tr key={item.vendorId} className={dlTableRowClass}>
                      <td className={dlTableCellClass}>
                        <p className="truncate font-medium text-gray-900 dark:text-white">
                          {item.doctorName}
                        </p>
                        <p className="truncate text-xs text-gray-500">
                          {[item.clinicName, item.city].filter(Boolean).join(' · ') || item.vendorId}
                        </p>
                      </td>
                      <td className={dlTableCellClass}>
                        <div className="flex flex-wrap gap-1">
                          {(['online', 'in-clinic'] as DeepLinkConsultationMode[]).map((mode) => {
                            const m = item.modes[mode];
                            if (!m?.available) return null;
                            return (
                              <span
                                key={mode}
                                className={`rounded-md px-2 py-0.5 text-xs ${
                                  m.seeded
                                    ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200'
                                    : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                                }`}
                              >
                                {modeLabel(mode)}
                                {m.seeded ? ' ✓' : ''}
                              </span>
                            );
                          })}
                        </div>
                      </td>
                      <td className={dlTableCellClass}>{statusBadge(item.seedStatus)}</td>
                      <td className={dlTableCellClass}>
                        {seededEntries.length ? (
                          <div className="space-y-1.5">
                            {seededEntries.map((entry) => (
                              <div key={entry.campaign.id} className="flex min-w-0 items-center gap-2">
                                <span className="w-14 shrink-0 text-[11px] text-gray-500">
                                  {entry.label}
                                </span>
                                <p className="min-w-0 flex-1 truncate font-mono text-[11px] text-[#128C7E] dark:text-[#25D366]">
                                  {entry.campaign.shortUrl || entry.campaign.shortCode || '—'}
                                </p>
                                <CopyLinkButton
                                  text={entry.campaign.shortUrl || ''}
                                  label="Copy"
                                />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">Not seeded yet</span>
                        )}
                      </td>
                      <td className={`${dlTableCellClass} text-right`}>
                        <ActionsDropdownMenu
                          open={openMenuKey === menuKey}
                          onToggle={() =>
                            setOpenMenuKey((prev) => (prev === menuKey ? null : menuKey))
                          }
                          onClose={() => setOpenMenuKey(null)}
                        >
                          {seededEntries.length ? (
                            <ActionMenuItem
                              icon={<Eye className="h-4 w-4" />}
                              label="View"
                              onClick={() => {
                                setOpenMenuKey(null);
                                setViewItem(item);
                              }}
                            />
                          ) : null}
                          {seededEntries.length && pendingModes.length ? <ActionMenuDivider /> : null}
                          {item.modes.online?.available && !item.modes.online.seeded ? (
                            <ActionMenuItem
                              icon={<Link2 className="h-4 w-4" />}
                              label="Seed online"
                              disabled={busy}
                              onClick={() => void handleSeed(item, ['online'])}
                            />
                          ) : null}
                          {item.modes['in-clinic']?.available && !item.modes['in-clinic'].seeded ? (
                            <ActionMenuItem
                              icon={<Link2 className="h-4 w-4" />}
                              label="Seed in-clinic"
                              disabled={busy}
                              onClick={() => void handleSeed(item, ['in-clinic'])}
                            />
                          ) : null}
                          {pendingModes.length > 1 ? (
                            <ActionMenuItem
                              icon={<Link2 className="h-4 w-4 text-[#128C7E]" />}
                              label="Seed both"
                              disabled={busy}
                              onClick={() => void handleSeed(item, pendingModes)}
                            />
                          ) : null}
                          {!seededEntries.length && !pendingModes.length ? (
                            <ActionMenuItem
                              icon={<Link2 className="h-4 w-4" />}
                              label="No actions"
                              disabled
                              onClick={() => setOpenMenuKey(null)}
                            />
                          ) : null}
                        </ActionsDropdownMenu>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      <DeepLinkSeededCampaignsViewModal
        open={Boolean(viewItem) && viewEntries.length > 0}
        title={viewItem?.doctorName || 'Doctor links'}
        subtitle={[viewItem?.clinicName, viewItem?.city].filter(Boolean).join(' · ')}
        entries={viewEntries}
        onClose={() => setViewItem(null)}
      />
    </div>
  );
};

export default DeepLinkDoctorSeedPanel;
